import { Box, Button, Grid, Typography } from "@material-ui/core";
import Dialog from "components/Dialog";
import { LinearBuffer } from "components/LinearbBuffer";
import DownloadTemplateButton from "components/ParcelTemplate/DownloadTemplateButton";
import UploadTemplateButton from "components/ParcelTemplate/UploadTemplateButton";
import SearchBox from "components/SearchBox";
import PARCEL_TEMPLATE_HEADER from "config/parcel-template";
import { theme } from "config/theme";
import useText from "constants/text";
import { NewJobContext } from "domains/Job/context";
import { useOrderStyles } from "domains/Parcel/style";
import { isWarring } from "domains/Parcel/utils/validator";
import NewReceiver from "domains/Receiver/Components/NewReceiver";
import { Order } from "generated/graphql";
import { useCurrentUser } from "hooks";
import { DeleteBtnIcon, OrderIcon, OrderIconPhone } from "icons";
import { isEmpty } from "lodash";
import React, { useEffect } from "react";
import "styled-components/macro";
import ActionsGroup from "ui/ActionGroup";
import { makeSearch, parseTemplate, uuid } from "utils";
import { TemplateResultType } from "../utils";
import AddParcel from "./AddParcel";
import { GlobalButtons } from "./JobStyled";
import OrderCollapse from "./OrderCollapse";
import UpdateOrder from "./UpdateOrder";

interface IProps {
  onNext?: React.Dispatch<React.SetStateAction<number>>;
  cancelUpdate: () => void;
}

export function OrdersList({ onNext }: IProps) {
  const { user } = useCurrentUser();
  const { orders, setOrders, job } = React.useContext(NewJobContext);
  const [open, setOpen] = React.useState(false);
  const [openUpdate, setOpenUpdate] = React.useState(false);
  const [order, setOrder] = React.useState<Order>({ order_parcels: [], order_receiver: {} });
  const [currentOrder, setCurrentOrder] = React.useState<Order>(null);
  const [openParcel, setOpenParcel] = React.useState(false);
  const [isOrderDetail, setIsOrderDetail] = React.useState(false);
  const styles = useOrderStyles();
  const { text } = useText();
  const [isUploading, setIsUploading] = React.useState(false);
  const uploadTemplateButtonRef = React.useRef<HTMLButtonElement>();
  const downloadTemplateButtonRef = React.useRef<HTMLButtonElement>();
  const [search, setValue] = React.useState<string>("");
  const [checkOrder, setCheckOrder] = React.useState<string[]>([]);
  const [confirmDeletion, setConfirmDeletion] = React.useState(false);
  const [ordersToDelete, setOrdersToDelete] = React.useState([]);
  const [receiverSimilairyPhoneNumber, setReceiverSimilairyPhoneNumber] = React.useState(false);

  const searchResult: Order[] = React.useMemo(
    () => (search ? makeSearch(orders, search.replace(/\s/g, "")) : orders),
    [search, orders]
  );

  const fixReceiverSimilairyPhoneNumber = (phone: string) => {
    searchResult.map((order) => {
      if (order?.order_receiver?.receiver_phone_number === phone) {
        setReceiverSimilairyPhoneNumber(true);
      } else {
        setReceiverSimilairyPhoneNumber(false);
      }
    });
  };

  const handleAddParcel = (order: any) => {
    setOrders((prevState) => {
      const newState = prevState.map((row) => {
        if (row._id === order._id) {
          return order;
        }
        return row;
      });
      return [...newState];
    });
    setIsOrderDetail(false);
  };

  const onClose = () => setOpenParcel(false);
  const isOktoGoNext = React.useMemo(() => {
    let isOk = true;
    if (orders?.length === 0) return false;
    orders?.forEach((element) => {
      const value = isWarring(element);
      if (value === true) {
        isOk = false;
      }
      if (element.order_parcels?.length === 0) {
        isOk = false;
      }
      const parcel = element.order_parcels?.find(
        // eslint-disable-next-line camelcase
        ({ parcel_description, parcel_size }) => isEmpty(parcel_description) || isEmpty(parcel_size)
      );
      if (parcel) {
        isOk = false;
      }
    });

    return isOk;
  }, [orders]);

  const handleUploadOrder = ({ data }: TemplateResultType) => {
    setOrders(data);
  };

  const onAddParcel = (order: Order) => {
    setCurrentOrder(order);
    setOpenParcel(true);
    setIsOrderDetail(true);
  };

  const handleDelete = (order: Order) => {
    const list: Order[] = orders.filter((o) => o._id !== order._id);
    setOrders(list);
  };

  const handleDuplicate = (data: Order) => {
    const newOrders: Order = {
      _id: uuid(),
      order_receiver: {
        ...data.order_receiver,
        receiver_phone_number: "",
      },
      order_parcels: [...data.order_parcels],
    };
    setOrders([newOrders, ...orders]);
  };

  const onEdit = (data: Order) => {
    setOrder(data);
    setOpenUpdate(true);
  };

  const actionsGroups = [
    { key: "1", label: text.all.add_new_order },
    { key: "2", label: text.all.upload_template },
    { key: "3", label: text.all.download_template },
  ];

  const handleActionsClick = (key: string) => {
    switch (key) {
      case "1":
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        onNewOrder();
        break;
      case "2":
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        handleClickUpload();
        break;
      case "3":
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        handleClickDawnload();
        break;
      default:
        break;
    }
  };

  const handleClickOrder = (id: string) => {
    if (ordersToDelete.includes(id)) {
      ordersToDelete.splice(ordersToDelete.indexOf(id), 1);
    } else {
      ordersToDelete.push(id);
    }
  };

  const handleDeleteOrders = () => {
    setOrders(searchResult.filter((order) => !ordersToDelete.includes(order._id)));
    setConfirmDeletion(false);
    setOrdersToDelete([]);
  };

  const handleClickDeleteOrders = () => {
    setConfirmDeletion(true);
  };

  const onNewOrder = () => {
    setOpen(true);
  };

  const handleCloseReceiverDialog = () => setOpen(false);

  const handleClickUpload = () => {
    uploadTemplateButtonRef?.current?.click();
  };

  const handleClickDawnload = () => {
    downloadTemplateButtonRef?.current?.click();
  };

  const onSearch = (event: any) => {
    event.preventDefault();
    event.persist();
    setValue(event.target.value);
  };

  useEffect(() => {}, [searchResult]);

  return (
    <>
      <div className={styles.root}>
        {!isUploading ? (
          <Grid className={styles.rootContent}>
            <Grid className={styles.title}>
              <Typography variant="h2" className={styles.step}>
                {text.all.step} 2
              </Typography>
            </Grid>
            <Box className={styles.addOrderDesc}>
              <Grid className={styles.line} spacing={4}>
                <Grid className={styles.jobType}>{text.parcel.add_client_to_be_delivered}</Grid>
              </Grid>
              <Grid item container xs={6} className={styles.addOrder}>
                <Box className={styles.searchOrder}>
                  <SearchBox
                    className={styles.search}
                    value={search}
                    onChange={onSearch}
                    placeholder={text.all.placeholder}
                  />

                  {ordersToDelete?.length > 0 ? (
                    <div className={styles.delete}>
                      <Button
                        onClick={handleClickDeleteOrders}
                        startIcon={<DeleteBtnIcon />}
                        className={styles.deleteBtn}
                      >
                        {" "}
                        Delete
                      </Button>
                    </div>
                  ) : (
                    ordersToDelete?.length === 0 && (
                      <ActionsGroup
                        actions={actionsGroups}
                        onClick={handleActionsClick}
                        buttonName={text.all.add_new_order}
                      />
                    )
                  )}
                </Box>

                <Grid style={{ display: "none" }}>
                  <DownloadTemplateButton
                    variant="outlined"
                    css={`
                      margin-right: 10px;
                      &.MuiButton-outlined {
                        border: 1px solid ${theme.palette.primary.main};
                        color: ${theme.palette.primary.main};
                      }
                    `}
                    buttonRef={downloadTemplateButtonRef}
                  >
                    {text.all.download_template}
                  </DownloadTemplateButton>
                  <UploadTemplateButton
                    isloadingFunc={setIsUploading}
                    parseActionFunc={parseTemplate}
                    templateType={PARCEL_TEMPLATE_HEADER}
                    variant="contained"
                    color="primary"
                    entityList={orders}
                    onUpload={(uploadOrders) => handleUploadOrder(uploadOrders)}
                    buttonRef={uploadTemplateButtonRef}
                  >
                    {text.all.upload_template}
                  </UploadTemplateButton>
                </Grid>
              </Grid>
            </Box>
            <Grid className={styles.ordersDisplay}>
              {!orders.length && (
                <div className={styles.orderIcon}>
                  {[theme.breakpoints.down("xs")] ? <OrderIconPhone /> : <OrderIcon />}
                  <Grid className={styles.orderTextInfo}>{text.all.orderTextInfo}</Grid>
                </div>
              )}
              {searchResult?.map((view: Order) => (
                <div
                  css={`
                    width: 117.7%;
                  `}
                >
                  <OrderCollapse
                    order={view}
                    onEdit={onEdit}
                    onDelete={handleDelete}
                    onAddParcel={onAddParcel}
                    onDuplicate={handleDuplicate}
                    handleAddParcel={handleAddParcel}
                    setCheckedOrder={setCheckOrder}
                    checkedOrder={checkOrder}
                    handleClickOrder={handleClickOrder}
                  />
                </div>
              ))}
            </Grid>
          </Grid>
        ) : (
          <Grid className={styles.rootContent}>
            <LinearBuffer />
          </Grid>
        )}
        <GlobalButtons>
          <Button color="primary" variant="outlined" className={styles.previousOrderList} onClick={() => onNext(0)}>
            {text.all.previous}
          </Button>
          <Button
            color="primary"
            variant="contained"
            className={styles.previousOrderList}
            css={`
              margin-top: 5px;
              &.MuiButton-root {
                width: 160px;
                margin-left: 20px;
                margin-right: 7px;
              }
            `}
            onClick={() => onNext(2)}
            disabled={!isOktoGoNext}
          >
            {text.all.next}
          </Button>
        </GlobalButtons>
      </div>
      <NewReceiver
        open={open}
        handleClose={handleCloseReceiverDialog}
        orders={orders}
        setOrders={setOrders}
        job={job}
        clientId={user?.company_user_clientId}
      />
      <UpdateOrder
        open={openUpdate}
        handleClose={() => setOpenUpdate(false)}
        order={order}
        clientId={user?.company_user_clientId}
        fixReceiverSimilairyPhoneNumber={fixReceiverSimilairyPhoneNumber}
        receiverSimilairyPhoneNumber={receiverSimilairyPhoneNumber}
      />
      {isOrderDetail && (
        <AddParcel
          open={openParcel}
          handleClose={onClose}
          order={currentOrder}
          user={user}
          handleAddParcel={handleAddParcel}
          job={job}
        />
      )}

      <Dialog
        title={text.all.confirmation}
        open={confirmDeletion}
        confirmButtonTitle={text.all.confirm}
        handleClose={() => setConfirmDeletion(false)}
        handleConfirm={handleDeleteOrders}
      >
        {text.all.delete_all_orders}
      </Dialog>
    </>
  );
}

export default OrdersList;
